import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { MapContainer, TileLayer, Polyline, CircleMarker, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import adminApi from '../services/adminApi';

function toDateInputValue(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function formatDateTime(value) {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '-' : date.toLocaleString();
}

function decodeGooglePolyline(encoded) {
  if (!encoded) return [];
  const points = [];
  let index = 0;
  let lat = 0;
  let lng = 0;
  while (index < encoded.length) {
    let b;
    let shift = 0;
    let result = 0;
    do {
      b = encoded.charCodeAt(index++) - 63;
      result |= (b & 0x1f) << shift;
      shift += 5;
    } while (b >= 0x20);
    const dlat = (result & 1) ? ~(result >> 1) : (result >> 1);
    lat += dlat;
    shift = 0;
    result = 0;
    do {
      b = encoded.charCodeAt(index++) - 63;
      result |= (b & 0x1f) << shift;
      shift += 5;
    } while (b >= 0x20);
    const dlng = (result & 1) ? ~(result >> 1) : (result >> 1);
    lng += dlng;
    points.push([lat * 1e-5, lng * 1e-5]);
  }
  return points;
}

function FitBounds({ bounds }) {
  const map = useMap();
  useEffect(() => {
    if (!bounds || bounds.length < 2) return;
    try {
      map.fitBounds(bounds, { padding: [24, 24] });
    } catch (_) {}
  }, [bounds, map]);
  return null;
}

function isRejectedPoint(point) {
  if (point.lat === null || point.lng === null) return true;
  if (point.accuracy && point.accuracy > 100) return true;
  if (point.speed && point.speed > 120) return true;
  return false;
}

function formatNetworkLabel(network) {
  if (!network) return '-';
  if (typeof network === 'object') {
    return network.type || network.connectionType || network.state || JSON.stringify(network);
  }
  return String(network);
}

export default function AdminLocationDebug() {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedDate, setSelectedDate] = useState(() => toDateInputValue(new Date()));
  const [showFilteredOnly, setShowFilteredOnly] = useState(false);
  const [selectedTripIndex, setSelectedTripIndex] = useState(null);
  const [rawPage, setRawPage] = useState(1);
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const q = searchQuery.trim();
    if (q.length < 2) {
      setSearchResults([]);
      return undefined;
    }

    const timer = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const resp = await adminApi.searchLocationUsers(q);
        setSearchResults(resp?.users || []);
      } catch (err) {
        setSearchResults([]);
        setError(err?.response?.data?.message || err?.message || 'Search failed');
      } finally {
        setSearchLoading(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  const loadDay = useCallback(async (page = 1) => {
    if (!selectedEmployee?.employeeId) return;
    setLoading(true);
    setError('');
    try {
      const resp = await adminApi.fetchLocationUserDay(selectedEmployee.employeeId, {
        date: selectedDate,
        page,
        limit: 200
      });
      setPayload(resp);
      setRawPage(page);
      setSelectedTripIndex(null);
    } catch (err) {
      setPayload(null);
      setError(err?.response?.data?.message || err?.message || 'Failed to load location data');
    } finally {
      setLoading(false);
    }
  }, [selectedEmployee, selectedDate]);

  useEffect(() => {
    if (selectedEmployee?.employeeId) {
      loadDay(1);
    }
  }, [selectedEmployee, selectedDate, loadDay]);

  const summary = payload?.summary || {};
  const diagnostics = summary?.diagnostics || {};
  const trips = payload?.trips || [];
  const trackPoints = payload?.trackPoints || [];
  const gpsStatusLogs = payload?.gpsStatusLogs || [];
  const rawPoints = payload?.rawPoints?.points || [];

  const mapPoints = useMemo(() => {
    const fromRaw = rawPoints
      .filter((p) => p.lat !== null && p.lng !== null)
      .map((p) => ({
        lat: p.lat,
        lng: p.lng,
        rejected: isRejectedPoint(p),
        mocked: p.mocked,
        accuracy: p.accuracy
      }));

    if (fromRaw.length) {
      return showFilteredOnly ? fromRaw.filter((p) => !p.rejected) : fromRaw;
    }

    return trackPoints
      .filter((p) => p.lat !== null && p.lng !== null)
      .map((p) => ({
        lat: p.lat,
        lng: p.lng,
        rejected: false,
        mocked: false,
        accuracy: p.accuracy
      }));
  }, [rawPoints, trackPoints, showFilteredOnly]);

  const tripPolylines = useMemo(() => {
    return trips.map((trip, index) => ({
      index,
      positions: decodeGooglePolyline(trip.encodedPolyline || trip.snappedPolyline),
      selected: selectedTripIndex === index
    })).filter((trip) => trip.positions.length > 1);
  }, [trips, selectedTripIndex]);

  const mapBounds = useMemo(() => {
    const coords = [];
    mapPoints.forEach((p) => coords.push([p.lat, p.lng]));
    tripPolylines.forEach((trip) => trip.positions.forEach((pos) => coords.push(pos)));
    if (!coords.length) return null;
    return L.latLngBounds(coords);
  }, [mapPoints, tripPolylines]);

  const networkEntries = Object.entries(diagnostics.network || {});
  const providerEntries = Object.entries(diagnostics.provider || {});

  const handleCopyRaw = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(rawPoints, null, 2));
    } catch (_) {}
  };

  return (
    <div className="admin-location-debug">
      <div className="admin-inline-controls admin-location-controls">
        <div className="admin-location-search-wrap">
          <label htmlFor="admin-location-search">Employee</label>
          <input
            id="admin-location-search"
            type="search"
            placeholder="Search by ID, email, or name"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            autoComplete="off"
          />
          {searchLoading && <span className="admin-location-search-hint">Searching…</span>}
          {!!searchResults.length && !selectedEmployee && (
            <div className="admin-location-search-results" role="listbox">
              {searchResults.map((user) => (
                <button
                  key={user.employeeId}
                  type="button"
                  className="admin-location-search-item"
                  onClick={() => {
                    setSelectedEmployee(user);
                    setSearchQuery(`${user.name} (#${user.employeeId})`);
                    setSearchResults([]);
                  }}
                >
                  <strong>{user.name}</strong>
                  <span>#{user.employeeId} · {user.email}</span>
                  <span>{(user.companies || []).map((c) => c.name).join(', ') || 'No companies'}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        <div>
          <label htmlFor="admin-location-date">Date</label>
          <input
            id="admin-location-date"
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
          />
        </div>

        <div className="admin-location-toggle">
          <label>
            <input
              type="checkbox"
              checked={showFilteredOnly}
              onChange={(e) => setShowFilteredOnly(e.target.checked)}
            />
            Show filtered-only on map
          </label>
        </div>

        <button
          type="button"
          className="btn-secondary small"
          onClick={() => loadDay(rawPage)}
          disabled={!selectedEmployee || loading}
        >
          Refresh
        </button>
      </div>

      {selectedEmployee && (
        <div className="admin-location-selected">
          <strong>{selectedEmployee.name}</strong>
          <span>#{selectedEmployee.employeeId}</span>
          <span>{selectedEmployee.email}</span>
          <button
            type="button"
            className="btn-secondary small"
            onClick={() => {
              setSelectedEmployee(null);
              setSearchQuery('');
              setPayload(null);
            }}
          >
            Clear
          </button>
        </div>
      )}

      {error && <div className="admin-error" role="alert">{error}</div>}

      {loading && (
        <div className="modern-loading-state">
          <div className="loading-spinner-container">
            <div className="loading-spinner modern" />
            <div className="loading-text">
              <h3>Loading location debug data</h3>
              <p>Fetching trips, raw GPS, and diagnostics.</p>
            </div>
          </div>
        </div>
      )}

      {!loading && payload && (
        <>
          <div className="admin-location-summary-grid">
            <div>
              <span>Data source</span>
              <strong>{summary.dataSource || '-'}{summary.rawDataSource && summary.rawDataSource !== summary.dataSource ? ` (raw: ${summary.rawDataSource})` : ''}</strong>
            </div>
            <div>
              <span>Raw points</span>
              <strong>{diagnostics.totalRaw ?? 0}</strong>
            </div>
            <div>
              <span>Used for trips</span>
              <strong>{diagnostics.filteredCount ?? 0}</strong>
            </div>
            <div>
              <span>Rejected</span>
              <strong>
                {(diagnostics.rejected?.missingCoords || 0)
                  + (diagnostics.rejected?.accuracyTooHigh || 0)
                  + (diagnostics.rejected?.speedTooHigh || 0)}
              </strong>
            </div>
            <div>
              <span>Trips</span>
              <strong>{summary.tripCount ?? 0}</strong>
            </div>
            <div>
              <span>Total distance</span>
              <strong>{Number(summary.totalDistanceKm || 0).toFixed(2)} km</strong>
            </div>
            <div>
              <span>GPS off events</span>
              <strong>{summary.gpsOffCount ?? 0}</strong>
            </div>
            <div>
              <span>Largest gap</span>
              <strong>{diagnostics.largestGapMinutes ? `${diagnostics.largestGapMinutes} min` : '-'}</strong>
            </div>
            <div>
              <span>Mocked points</span>
              <strong>{diagnostics.mockedCount ?? 0}</strong>
            </div>
            <div>
              <span>Avg battery</span>
              <strong>{diagnostics.avgBattery != null ? diagnostics.avgBattery : '-'}</strong>
            </div>
          </div>

          <div className="admin-location-panels">
            <div className="admin-location-map-panel">
              <h3>Track map</h3>
              <div className="admin-location-map-shell">
                <MapContainer
                  center={mapPoints.length ? [mapPoints[0].lat, mapPoints[0].lng] : [20.5937, 78.9629]}
                  zoom={mapPoints.length ? 13 : 4}
                  scrollWheelZoom
                  style={{ height: '100%', width: '100%' }}
                >
                  <TileLayer
                    attribution="&copy; OpenStreetMap contributors"
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  {mapBounds && <FitBounds bounds={mapBounds} />}
                  {mapPoints.map((point, idx) => (
                    <CircleMarker
                      key={`pt-${idx}`}
                      center={[point.lat, point.lng]}
                      radius={point.rejected ? 3 : 4}
                      pathOptions={{
                        color: point.mocked ? '#dc3545' : point.rejected ? '#adb5bd' : '#0d6efd',
                        fillColor: point.mocked ? '#dc3545' : point.rejected ? '#adb5bd' : '#0d6efd',
                        fillOpacity: 0.8,
                        weight: 1
                      }}
                    />
                  ))}
                  {tripPolylines.map((trip) => (
                    <Polyline
                      key={`trip-${trip.index}`}
                      positions={trip.positions}
                      pathOptions={{
                        color: '#000000',
                        weight: trip.selected ? 6 : 4,
                        opacity: trip.selected ? 1 : 0.9
                      }}
                    />
                  ))}
                </MapContainer>
              </div>
              <div className="admin-location-legend">
                <span className="legend-good">Valid point</span>
                <span className="legend-muted">Rejected</span>
                <span className="legend-bad">Mocked</span>
                <span className="legend-trip">Trip path</span>
              </div>
            </div>

            <div className="admin-location-side-panels">
              <div className="admin-location-card">
                <h3>Trips</h3>
                <div className="admin-table-wrap">
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>Start</th>
                        <th>End</th>
                        <th>Distance</th>
                        <th>Duration</th>
                        <th>Mode</th>
                      </tr>
                    </thead>
                    <tbody>
                      {trips.length ? trips.map((trip, index) => (
                        <tr
                          key={`${trip.startTime}-${index}`}
                          className={selectedTripIndex === index ? 'selected-row' : ''}
                          onClick={() => setSelectedTripIndex(index)}
                          style={{ cursor: 'pointer' }}
                        >
                          <td>{formatDateTime(trip.startTime)}</td>
                          <td>{formatDateTime(trip.endTime)}</td>
                          <td>{Number(trip?.stats?.distance || 0).toFixed(2)} km</td>
                          <td>{Number(trip?.stats?.duration || 0).toFixed(1)} min</td>
                          <td>{trip?.stats?.transportationMode || '-'}</td>
                        </tr>
                      )) : (
                        <tr>
                          <td colSpan={5}>No trips detected for this day.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="admin-location-card">
                <h3>GPS / network</h3>
                <div className="admin-location-diagnostics">
                  <div>
                    <h4>Network</h4>
                    {networkEntries.length ? networkEntries.map(([key, count]) => (
                      <div key={key} className="admin-location-kv">
                        <span>{key}</span>
                        <strong>{count}</strong>
                      </div>
                    )) : <p className="admin-location-empty">No network metadata.</p>}
                  </div>
                  <div>
                    <h4>Provider</h4>
                    {providerEntries.length ? providerEntries.map(([key, count]) => (
                      <div key={key} className="admin-location-kv">
                        <span>{key}</span>
                        <strong>{count}</strong>
                      </div>
                    )) : <p className="admin-location-empty">No provider metadata.</p>}
                  </div>
                  <div>
                    <h4>Upload gaps (&gt;10 min)</h4>
                    {(diagnostics.gaps || []).length ? diagnostics.gaps.map((gap, idx) => (
                      <div key={idx} className="admin-location-gap">
                        <span>{formatDateTime(gap.from)} → {formatDateTime(gap.to)}</span>
                        <strong>{gap.gapMinutes} min</strong>
                      </div>
                    )) : <p className="admin-location-empty">No large gaps detected.</p>}
                  </div>
                  <div>
                    <h4>GPS off timeline</h4>
                    {gpsStatusLogs.length ? gpsStatusLogs.map((log) => (
                      <div key={log.id} className="admin-location-gap">
                        <span>{formatDateTime(log.timestamp)}</span>
                        <strong>GPS off</strong>
                      </div>
                    )) : <p className="admin-location-empty">No GPS-off events logged.</p>}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="admin-location-card">
            <div className="admin-location-raw-header">
              <h3>Raw data</h3>
              <div className="admin-location-raw-actions">
                <button type="button" className="btn-secondary small" onClick={handleCopyRaw}>
                  Copy page JSON
                </button>
                <button
                  type="button"
                  className="btn-secondary small"
                  disabled={rawPage <= 1}
                  onClick={() => loadDay(rawPage - 1)}
                >
                  Prev
                </button>
                <span>
                  Page {payload?.rawPoints?.page || 1} / {payload?.rawPoints?.totalPages || 1}
                  {' '}({payload?.rawPoints?.total || 0} total)
                </span>
                <button
                  type="button"
                  className="btn-secondary small"
                  disabled={rawPage >= (payload?.rawPoints?.totalPages || 1)}
                  onClick={() => loadDay(rawPage + 1)}
                >
                  Next
                </button>
              </div>
            </div>
            <div className="admin-table-wrap admin-location-raw-table-wrap">
              <table className="admin-table admin-location-raw-table">
                <thead>
                  <tr>
                    <th>Time</th>
                    <th>Lat</th>
                    <th>Lng</th>
                    <th>Accuracy</th>
                    <th>Speed</th>
                    <th>Mocked</th>
                    <th>Network</th>
                    <th>Provider</th>
                    <th>Battery</th>
                  </tr>
                </thead>
                <tbody>
                  {rawPoints.length ? rawPoints.map((point, idx) => (
                    <tr key={`${point.timestamp}-${idx}`} className={isRejectedPoint(point) ? 'admin-location-rejected-row' : ''}>
                      <td>{formatDateTime(point.timestamp)}</td>
                      <td>{point.lat != null ? Number(point.lat).toFixed(6) : '-'}</td>
                      <td>{point.lng != null ? Number(point.lng).toFixed(6) : '-'}</td>
                      <td>{point.accuracy != null ? `${Number(point.accuracy).toFixed(0)}m` : '-'}</td>
                      <td>{point.speed != null ? Number(point.speed).toFixed(1) : '-'}</td>
                      <td>{point.mocked ? 'Yes' : 'No'}</td>
                      <td>{formatNetworkLabel(point.network)}</td>
                      <td>{point.provider || '-'}</td>
                      <td>{point.batteryLevel != null ? Number(point.batteryLevel).toFixed(2) : '-'}</td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan={9}>No raw points for this page.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="admin-location-reject-breakdown">
              <span>Rejected breakdown:</span>
              <span>missing coords {diagnostics.rejected?.missingCoords || 0}</span>
              <span>accuracy &gt;100m {diagnostics.rejected?.accuracyTooHigh || 0}</span>
              <span>speed &gt;120 {diagnostics.rejected?.speedTooHigh || 0}</span>
            </div>
          </div>
        </>
      )}

      {!loading && !payload && selectedEmployee && !error && (
        <p className="admin-location-empty">No data loaded yet.</p>
      )}

      {!selectedEmployee && !loading && (
        <p className="admin-location-empty">Search and select an employee to inspect location data.</p>
      )}
    </div>
  );
}
